import { IonCol, IonGrid, IonRow } from '@ionic/react';
import React, { useMemo, useState } from 'react';

import moment from 'moment';

import { downloadCarnet, downloadPoliza } from '../../../axios/Pet';
import { usePetPic } from '../../../hooks/Pet/usePetUtils';
import { useGetPlanesByMascota } from '../../../hooks/Plan/usePlanes';
import { useDynamicTheme } from '../../../theme/useDynamicTheme';
import { formatDateDDMMYYYY } from '../../../utils/date';
import { writeFile } from '../../../utils/filesystem';
import { capitalizeFirstLetter } from '../../../utils/string';
import Button from '../../UI/Button';
import Spinner from '../../UI/Spinner';
import Toast from '../../UI/Toast';
import './Carnet.scss';
import { CarnetProps } from './types';

const Carnet: React.FC<CarnetProps> = ({ pet }) => {
  const { theme } = useDynamicTheme();
  const { id, raza, nombre, fecha_nacimiento } = pet || {};
  const { planMascota, isValidating } = useGetPlanesByMascota(null, nombre);
  const { plan, poliza, date } = planMascota || {};

  const { petImage } = usePetPic(pet);
  const [carnetLoading, setCarnetLoading] = useState<boolean>(false);
  const [carnetSuccess, setCarnetSuccess] = useState<boolean>(false);
  const [polizaLoading, setPolizaLoading] = useState<boolean>(false);
  const [polizaSuccess, setPolizaSuccess] = useState<boolean>(false);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>('');

  const vigencia = useMemo(() => {
    if (!date) return { desde: '', hasta: '' };
    const fecha = moment(date, 'YYYY-MM-DD HH:mm:ss.SSSSSS');
    return {
      desde: fecha.format('MM/YY'),
      hasta: fecha.clone().add(1, 'year').format('MM/YY'),
    };
  }, [date]);

  const blobToDataUrl = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          resolve(reader.result);
          return;
        }
        reject(new Error('No se pudo leer el archivo descargado'));
      };
      reader.onerror = () => reject(new Error('No se pudo leer el archivo descargado'));
      reader.readAsDataURL(blob);
    });

  const renderDownloadContent = (
    isLoading: boolean,
    isSuccess: boolean,
    defaultLabel: string,
  ) => {
    if (isLoading) return <Spinner color="light" />;
    if (isSuccess) {
      return (
        <span className="download-success">
          <svg className="checkmark" viewBox="0 0 24 24">
            <path className="checkmark-path" d="M4 12l6 6L20 6" />
          </svg>
          ¡Descargado!
        </span>
      );
    }

    return defaultLabel;
  };

  const handleDownloadCarnet = async () => {
    try {
      if (!id) throw new Error('No se encontró el id de la mascota');

      setCarnetLoading(true);
      setCarnetSuccess(false);
      const response = await downloadCarnet(id);
      const mimeType = 'application/pdf';
      const blob = new Blob([response.data], { type: mimeType });
      const dataUrl = await blobToDataUrl(blob);
      const fileName = `carnet_mascota_${id}.pdf`;

      await writeFile({
        fileName,
        data: dataUrl,
        mimeType,
      });

      setToastMessage('Carnet guardado en descargas');
      setShowToast(true);
      setCarnetSuccess(true);
      setTimeout(() => setCarnetSuccess(false), 3500);
    } catch (error) {
      console.error('Error al descargar el carnet:', error);
    } finally {
      setCarnetLoading(false);
    }
  };

  const handleDownloadPoliza = async () => {
    try {
      if (!id) throw new Error('No se encontró el id de la mascota');

      setPolizaLoading(true);
      setPolizaSuccess(false);
      const response = await downloadPoliza(id);
      const mimeType = 'application/pdf';
      const blob = new Blob([response.data], { type: mimeType });
      const dataUrl = await blobToDataUrl(blob);
      const fileName = `poliza_${nombre || id}_${moment().format('DD-MM-YYYY')}.pdf`;

      await writeFile({
        fileName,
        data: dataUrl,
        mimeType,
      });

      setToastMessage('Póliza guardada en descargas');
      setShowToast(true);
      setPolizaSuccess(true);
      setTimeout(() => setPolizaSuccess(false), 3500);
    } catch (error) {
      console.error('Error al descargar la póliza:', error);
    } finally {
      setPolizaLoading(false);
    }
  };

  return (
    <div>
      {isValidating && <Spinner fullscreen />}

      <div className="carnet-wrapper">
        <div className="carnet-mascota">
          {/* Header */}
          <div className="carnet-header">
            <div className="carnet-logo">
              <span className="logo-text">{theme?.brandName}</span>
            </div>
            <div className="carnet-title">
              <h2>Carnet de Asegurado</h2>
            </div>
          </div>

          {/* Body */}
          <div className="carnet-body">
            <IonGrid className="ion-no-padding">
              <IonRow>
                {/* Columna izquierda: Imagen y datos de la mascota */}
                <IonCol size="5" className="carnet-left-col">
                  <div className="carnet-image-wrapper">
                    <img src={petImage} alt={nombre} className="carnet-pet-image" />
                  </div>

                  <div className="carnet-pet-info">
                    <div className="pet-info-item">
                      <span className="pet-info-value">{nombre}</span>
                    </div>

                    <div className="pet-info-item">
                      <span className="pet-info-label">{raza}</span>
                    </div>
                  </div>
                </IonCol>

                {/* Columna derecha: Información del seguro */}
                <IonCol size="7" className="carnet-info-col">
                  <div className="carnet-info">
                    <div className="info-item">
                      <span className="info-label">Fecha de Nacimiento:</span>
                      <span className="info-value">
                        {fecha_nacimiento ? formatDateDDMMYYYY(fecha_nacimiento) : '-'}
                      </span>
                    </div>

                    <div className="info-item">
                      <span className="info-label">Plan:</span>
                      <span className="info-value">
                        {plan ? capitalizeFirstLetter(plan.toLowerCase()) : '-'}
                      </span>
                    </div>

                    <div className="info-item">
                      <span className="info-label">Vigencia:</span>
                      <span className="info-value">
                        {vigencia.desde} - {vigencia.hasta}
                      </span>
                    </div>

                    <div className="info-item">
                      <span className="info-label">Póliza:</span>
                      <span className="info-value">{poliza || '-'}</span>
                    </div>
                  </div>
                </IonCol>
              </IonRow>
            </IonGrid>
          </div>

          {/* Footer */}
          <div className="carnet-footer">
            <span className="emission-date">
              Emisión: {moment().format('DD/MM/YYYY')}
            </span>
          </div>
        </div>

        {/* Botón de descarga FUERA del ref */}
        <div className="carnet-actions">
          <Button
            noMarginTop
            marginBottom={false}
            onClick={handleDownloadCarnet}
            disabled={carnetLoading || carnetSuccess || polizaLoading}
          >
            {renderDownloadContent(carnetLoading, carnetSuccess, 'Descargar carnet')}
          </Button>

          <Button
            noMarginTop
            marginBottom={false}
            fill="outline"
            onClick={handleDownloadPoliza}
            disabled={polizaLoading || polizaSuccess || carnetLoading}
          >
            {renderDownloadContent(polizaLoading, polizaSuccess, 'Descargar póliza')}
          </Button>
        </div>
      </div>

      <Toast
        isOpen={showToast}
        onDidDismiss={() => setShowToast(false)}
        message={toastMessage}
        duration={4000}
        color="success"
      />
    </div>
  );
};

export default Carnet;
